import React, { useState, useId, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import styles from './Accordion.module.scss';
import ChevronIcon from '../../ChevronIcon/components/ChevronIcon';

const Accordion = ({
  title,
  defaultExpanded = false,
  children,
}) => {
  const [phase, setPhase] = useState(defaultExpanded ? 'expanded' : 'hidden');
  const panelRef = useRef(null);
  const rafRef = useRef(null);
  const id = useId();
  const triggerId = `${id}-trigger`;
  const panelId = `${id}-panel`;

  const isExpanded = phase === 'expanded' || phase === 'appearing';

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (phase === 'appearing') {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setPhase('expanded');
        });
      });
    }
  }, [phase]);

  const handleToggle = useCallback(() => {
    setPhase((prev) => {
      if (prev === 'expanded') return 'collapsing';
      if (prev === 'hidden') return 'appearing';
      return prev;
    });
  }, []);

  const handleTransitionEnd = useCallback((e) => {
    if (e.propertyName === 'grid-template-rows' && phase === 'collapsing') {
      setPhase('hidden');
    }
  }, [phase]);

  const panelClassName = clsx(
    styles.Accordion__panel,
    phase !== 'hidden' && styles['Accordion__panel--visible'],
    (phase === 'expanded') && styles['Accordion__panel--expanded'],
  );

  return (
    <div className={styles.Accordion}>
      <button
        id={triggerId}
        className={styles.Accordion__trigger}
        type="button"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={handleToggle}
      >
        <span>{title}</span>
        <span
          className={clsx(
            styles.Accordion__icon,
            isExpanded && styles['Accordion__icon--expanded']
          )}
        >
          <ChevronIcon size="1em" decorative />
        </span>
      </button>

      <div
        id={panelId}
        ref={panelRef}
        role="region"
        aria-labelledby={triggerId}
        className={panelClassName}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className={styles.Accordion__panelInner}>
          <div className={styles.Accordion__content}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accordion;
